import React from 'react';
import { Switch } from '@headlessui/react';

interface ToggleProps {
  /** Whether the toggle is on. */
  enabled: boolean;
  /** Called when the toggle state changes. */
  onChange: (enabled: boolean) => void;
  /** Primary label. */
  label: string;
  /** Optional description below the label. */
  description?: string;
  /** Accessible screen reader label (defaults to label). */
  srLabel?: string;
  /** When true, the toggle is visually dimmed and non-interactive. */
  disabled?: boolean;
  /** Optional node rendered inline after the label (e.g., a feature gate badge). */
  labelSuffix?: React.ReactNode;
}

/**
 * Accessible toggle switch built on Headless UI Switch.
 * Renders with a label/description on the left and the switch on the right.
 */
export const Toggle: React.FC<ToggleProps> = ({
  enabled,
  onChange,
  label,
  description,
  srLabel,
  disabled = false,
  labelSuffix,
}) => {
  return (
    <Switch.Group>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <Switch.Label
              className={`text-sm font-medium ${
                disabled ? 'text-muted-foreground cursor-not-allowed' : 'text-foreground cursor-pointer'
              }`}
            >
              {label}
            </Switch.Label>
            {labelSuffix}
          </div>
          {description && (
            <Switch.Description
              className={`text-xs leading-relaxed text-muted-foreground ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {description}
            </Switch.Description>
          )}
        </div>
        <Switch
          checked={enabled}
          onChange={disabled ? () => {} : onChange}
          disabled={disabled}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
            enabled ? 'bg-volt' : 'bg-surface-raised'
          } ${disabled ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}
        >
          <span className="sr-only">{srLabel || label}</span>
          <span
            className={`inline-block h-5 w-5 transform rounded-full transition-transform ${
              enabled ? 'translate-x-5 bg-accent-foreground' : 'translate-x-0.5 bg-subtle-foreground'
            }`}
          />
        </Switch>
      </div>
    </Switch.Group>
  );
};
